import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import {
  getCampaignCluster,
  PostCampaignUmbrellaChildRequestType,
  postCampaignUmbrellaChild,
  postSKPStatus,
  PostSKPStatusRequestType,
  putCampaignUmbrellaChild,
  PutCampaignUmbrellaChildRequestType,
  deleteInventoryById,
  DeleteInventoryByIdRequestType,
} from 'features/CampaignAndDiscount/services/campaignUmbrella'
import {
  getVendors,
  type GetVendorListResponseInterface,
} from 'features/Enterprise/Vendor/services/vendorList'
import {
  getSKPDetailAPI,
  fetchSKPProducts,
  mapPaginatedListToLegacyQuery,
} from 'features/Enterprise/SKP/utils/apiList'
import { formatDate } from 'features/CampaignAndDiscount/utils/format'
import { toastSuccess } from 'utils/toast'
import { CampaignChildStatusType } from 'features/CampaignAndDiscount/@types/campaignUmbrellaChild'
import { getAllBrandV2 } from 'features/Product/services/brand'
import {
  SLICE_NAME,
  setIsLoading,
  setOption,
  setForm,
  setProductSKP,
  setProductSKPQuery,
  setBulkUpload,
} from './slice'

export const fetchCampaignOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchCampaignOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const [campaignCluster, vendors, brand] = await Promise.all([
        getCampaignCluster(),
        getVendors('page_size=50&page_index=1&company_name='),
        getAllBrandV2({ pageIndex: 1, pageSize: 50 }),
      ])

      const dataVendors = vendors.data as unknown as GetVendorListResponseInterface

      dispatch(
        setOption({
          campaignCluster: campaignCluster.data.data.campaign_clusters.map((cluster) => ({
            name: cluster.cluster,
            id: cluster.id,
          })),
          vendor: dataVendors.data.map((vendor) => ({
            name: vendor.company_name,
            id: vendor.id,
          })),
          brand: brand.data.data.map((brandItem) => ({
            name: brandItem.brand_name,
            id: brandItem.brand_id,
          })),
        }),
      )
      return {
        campaignCluster: campaignCluster.data,
        vendor: dataVendors.data,
        brand: brand.data,
      }
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)

      return rejectWithValue(error)
    }
  },
)

export const fetchVendorOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchVendorOptions`,
  async ({ companyName }: { companyName: string }, { dispatch, rejectWithValue }) => {
    try {
      const vendors = await getVendors(`page_size=50&page_index=1&company_name=${companyName}`)
      const dataVendors = vendors.data as unknown as GetVendorListResponseInterface

      dispatch(
        setOption({
          vendor: dataVendors.data.map((vendor) => ({
            name: vendor.company_name,
            id: vendor.id,
          })),
        }),
      )
      return {
        vendor: dataVendors.data,
      }
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)

      return rejectWithValue(error)
    }
  },
)

export const createCampaignUmbrellaChild = createAsyncThunk(
  `${SLICE_NAME}/createCampaignUmbrellaChild`,
  async (requestData: PostCampaignUmbrellaChildRequestType, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setIsLoading(true))
      const response = await postCampaignUmbrellaChild(requestData)

      dispatch(setIsLoading(false))
      toastSuccess(response.data.message)
      return response.data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchSKPDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchSKPDetail`,
  async (skpId: number, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await getSKPDetailAPI(skpId)
      const resData = data.data

      dispatch(
        setForm({
          campaignCluster: {
            id: resData.campaign_cluster_id.toString(),
            name: resData.campaing_cluster,
          },
          campaignName: resData.campaign_name,
          criteria: { id: resData.promotion_type, name: resData.promotion_type_name },
          endDate: formatDate(resData.end_date_campaign),
          endTime: formatDate(resData.end_date_campaign, 'TIME'),
          startDate: formatDate(resData.start_date_campaign),
          startTime: formatDate(resData.start_date_campaign, 'TIME'),
          vendor: { id: resData.vendor_id.toString(), name: resData.vendor_name },
          reimbursement: {
            method: resData.reimbursement_method,
            number: resData.reimbursement_number,
            name: resData.reimbursement_method_name,
          },
          status: resData.status as CampaignChildStatusType,
          skp_no: resData.skp_no,
          catmanFeedbackUrl: resData.catman_feedback_url ? resData.catman_feedback_url : null,
          submission_status: resData.submission_status || '',
          brand: { id: resData.brand_id?.toString() || '0', name: resData.brand_name || '' },
          is_budget_internal: resData.is_budget_internal || false,
          is_topup: resData.is_topup || false,
        }),
      )

      dispatch(
        setBulkUpload({
          code:
            resData.promotion_type === 'Rafaksi BMSM v2'
              ? 'SKP_CAMPAIGN_PLAN_BMSM'
              : 'SKP_CAMPAIGN_PLAN_SINGLE_DISCOUNT',
        }),
      )

      dispatch(setIsLoading(false))
      return data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const fetchProducts = createAsyncThunk(
  `${SLICE_NAME}/fetchProducts`,
  async ({ skpId }: { skpId: number }, { dispatch, rejectWithValue, getState }) => {
    const {
      campaignUmbrellaChild: {
        productSKP: { query },
      },
    } = getState() as StoreStateType
    dispatch(setIsLoading(true))

    // console.log(que)

    try {
      const pageSize = query.limit
      const { data } = await fetchSKPProducts(
        skpId,
        query.offset,
        query.limit.toString(),
        query.name.replaceAll(' ', '%20'),
      )
      const normalized = mapPaginatedListToLegacyQuery(data, pageSize, query.offset)

      dispatch(setProductSKP({ data: normalized.data }))
      if (query.isInitialFetch) {
        dispatch(
          setProductSKPQuery({
            isQuerySearchable: normalized.data.length > 0,
            isInitialFetch: false,
          }),
        )
      }
      dispatch(setIsLoading(false))

      return data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))

      return rejectWithValue(error)
    }
  },
)

export const updateSKPStatus = createAsyncThunk(
  `${SLICE_NAME}/updateSKPStatus`,
  async (payload: PostSKPStatusRequestType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await postSKPStatus(payload)
      dispatch(setIsLoading(false))
      return response.data
    } catch (error) {
      callErrorMsg(
        error,
        'Sedang mengalami kendala. Silakan coba beberapa saat lagi, atau hubungi engineer.',
        { decode: true },
      )
      //
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const updateCampaignUmbrellaChild = createAsyncThunk(
  `${SLICE_NAME}/updateCampaignUmbrellaChild`,
  async (
    {
      data,
      disableMsg = false,
    }: { data: PutCampaignUmbrellaChildRequestType; disableMsg?: boolean },
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsLoading(true))
    try {
      const response = await putCampaignUmbrellaChild(data)
      dispatch(setIsLoading(false))
      if (!disableMsg) toastSuccess(response.data.message)
      return response.data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const removeCampaignUmbrellaChildInventoryById = createAsyncThunk(
  `${SLICE_NAME}/removeCampaignUmbrellaChildInventoryById`,
  async ({ params, skpId }: DeleteInventoryByIdRequestType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const response = await deleteInventoryById({ params, skpId })
      dispatch(setIsLoading(false))
      toastSuccess(response.data.message)
      return response.data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const fetchBrandV2Options = createAsyncThunk(
  `${SLICE_NAME}/fetchBrandV2Options`,
  async ({ name }: { name: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await getAllBrandV2({
        pageSize: 50,
        pageIndex: 1,
        name,
      })

      dispatch(
        setOption({
          brand: response.data.data.map((brand) => ({
            name: brand.brand_name,
            id: brand.brand_id,
          })),
        }),
      )

      return {
        brand: response.data,
      }
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)

      return rejectWithValue(error)
    }
  },
)
